import { useRef, useState } from 'react'
import type { HubInboxItem, PayApp } from '../../api/types'
import { closeoutChecks } from './pulseCloseoutChecks.js'
import { recordPulseHandover, uploadWarrantyDocument, type PulseHandover } from './pulseApi'
import PulseHandoverForm from './PulseHandoverForm'

export default function PulseCloseout({
  projectId,
  revised,
  billed,
  collected,
  held,
  unpaidReleases,
  payApps,
  documents,
  handover,
  onOpenWorkBilled,
  onOpenPayments,
  onOpenRetainage,
  onRefresh,
}: {
  projectId: number
  revised: number
  billed: number
  collected: number
  held: number
  unpaidReleases: number
  payApps: PayApp[]
  documents: HubInboxItem[]
  handover: PulseHandover | null
  onOpenWorkBilled: () => void
  onOpenPayments: () => void
  onOpenRetainage: () => void
  onRefresh: () => void
}) {
  const [handoverOpen, setHandoverOpen] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const rows = closeoutChecks({
    revised, billed, collected, held, unpaidReleases, payApps, documents, handover,
  })

  function onCompleteClick(id: string) {
    if (id === 'work_billed') onOpenWorkBilled()
    if (id === 'payments_reconciled') onOpenPayments()
    if (id === 'retainage') onOpenRetainage()
  }

  return (
    <div data-testid="pulse-closeout">
      <div className="pulse-kicker">Prepare the final handoff</div>
      <ul className="pulse-closeout-list">
        {rows.map((r) => (
          <li key={r.id} data-testid={`pulse-closeout-${r.id}`} data-status={r.status}>
            {r.status === 'complete' && (r.id === 'work_billed' || r.id === 'payments_reconciled' || r.id === 'retainage') ? (
              <button type="button" className="pulse-closeout-open" onClick={() => onCompleteClick(r.id)}>
                <span className="pulse-closeout-label">{r.label}</span>
                <span className="pulse-closeout-status">Complete</span>
              </button>
            ) : (
              <div className="pulse-closeout-open">
                <span className="pulse-closeout-label">{r.label}</span>
                <span className="pulse-closeout-status">
                  {r.status === 'complete' ? 'Complete' : r.status === 'not_recorded' ? 'not recorded' : 'Incomplete'}
                </span>
              </div>
            )}
            {r.status === 'complete' && r.id === 'handover' && r.detail ? (
              <p className="pulse-closeout-helper">{r.detail}</p>
            ) : null}
            {r.status !== 'complete' && r.helper ? (
              <p className="pulse-closeout-helper">{r.helper}</p>
            ) : null}
            {r.id === 'warranty' && r.status !== 'complete' ? (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  hidden
                  data-testid="pulse-warranty-file"
                  onChange={async (e) => {
                    const file = e.target.files?.[0]
                    if (!file) return
                    await uploadWarrantyDocument(projectId, file)
                    onRefresh()
                  }}
                />
                <button type="button" className="btn b-dk btn-sm" data-testid="pulse-closeout-warranty-attach" onClick={() => fileRef.current?.click()}>
                  Attach warranty document
                </button>
              </>
            ) : null}
            {r.id === 'handover' && r.status !== 'complete' ? (
              handoverOpen ? (
                <PulseHandoverForm
                  onSave={async (input) => {
                    await recordPulseHandover(projectId, input)
                    setHandoverOpen(false)
                    onRefresh()
                  }}
                />
              ) : (
                <button type="button" className="btn b-dk btn-sm" data-testid="pulse-closeout-handover-open" onClick={() => setHandoverOpen(true)}>
                  Record handover
                </button>
              )
            ) : null}
          </li>
        ))}
      </ul>
      <p data-testid="pulse-closeout-next">Final closeout generation arrives in the next release.</p>
    </div>
  )
}
